import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faClock, faInfoCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Checkbox from "@ts-digital/vrc/es/components/checkbox";
import RadioGroup from "@ts-digital/vrc/es/components/radio-group";
import Toggle from "@ts-digital/vrc/es/components/toggle";
import classNames from "classnames";
import { compareAsc, endOfDay } from "date-fns";
import { find, includes, isEmpty, isNil, some } from "lodash";
import numbro from "numbro";
import { FormattedDate, FormattedNumber } from "react-intl";
import { GainsightEvent, trackEvent } from "../../../../core/common/gainsight";
import {
    PaymentPlanType,
    PaymentRequestStatus,
    PaymentStatus,
} from "../../../../PaymentContainerClient/commons/enums";
import PaymentRequestDto from "../../../../PaymentContainerClient/dtos/PaymentRequestDto";
import getFormattedMessageWithScope from "../../../../utils/getFormattedMessageWithScope";
import getInstallmentsSortedByDueDate from "../../../commons/getInstallmentsSortedByDueDate";
import { PrivacyCookiePolicyMenu } from "../../PrivacyCookiePolicyMenu";
import Tooltip, { TooltipTextPosition } from "../../Tooltip";
import { useWindowDimensions } from "../../WindowDimensionsProvider";
import "./style.css";

const fm = getFormattedMessageWithScope("components.PaymentForm.PaymentPlans");

interface PaymentPlansProps {
    paymentRequest: PaymentRequestDto;
    onSetPayNow: Function;
    isPayNow: boolean;
    onChangePaymentPlanId: Function;
    selectedPaymentPlanId?: string;
    onAddInstallmentId: Function;
    onRemoveInstallmentId: Function;
    selectedInstallmentIds: string[];
}

export const PaymentPlans = ({
    paymentRequest,
    onSetPayNow,
    isPayNow,
    onChangePaymentPlanId,
    selectedPaymentPlanId,
    onAddInstallmentId,
    onRemoveInstallmentId,
    selectedInstallmentIds,
}: PaymentPlansProps) => {
    const { width } = useWindowDimensions();

    const singleInstallmentPaymentPlan = find(
        paymentRequest.availablePaymentPlans,
        { type: PaymentPlanType.SingleInstallment }
    );
    const multipleInstallmentPaymentPlan = find(
        paymentRequest.availablePaymentPlans,
        { type: PaymentPlanType.MultipleInstallments }
    );

    function changePayNow(payNowValue: boolean) {
        trackEvent(GainsightEvent.ChangePayNow, { isPayNow: payNowValue });
        onSetPayNow(payNowValue);
    }

    function changePaymentPlanId(e: any) {
        const paymentPlan = find(paymentRequest.availablePaymentPlans, {
            id: e.target.value,
        });
        trackEvent(GainsightEvent.ChangeSelectedPaymentPlan, {
            paymentPlan: paymentPlan?.type,
        });
        onChangePaymentPlanId(e.target.value);
    }

    function addOrRemoveInstallmentId(id: string) {
        trackEvent(GainsightEvent.ChangeSelectedInstallments);

        if (!includes(selectedInstallmentIds, id)) {
            onAddInstallmentId(id);
        } else if (selectedInstallmentIds.length > 1) {
            onRemoveInstallmentId(id);
        }
    }

    // We need that the options are properly ordered and each option can exist
    // only if it is an available payment plan
    function getPaymentPlansOptions() {
        const options = [];

        if (!isNil(singleInstallmentPaymentPlan)) {
            const isOnHold = some(
                paymentRequest.payments,
                (payment) =>
                    payment.status === PaymentStatus.OnHold &&
                    includes(
                        payment.installmentIds,
                        singleInstallmentPaymentPlan.installments[0].id
                    )
            );

            options.push({
                onHold: isOnHold,
                label: (
                    <>
                        {isOnHold ? (
                            <Tooltip
                                text={fm("onHoldTooltip")}
                                position={TooltipTextPosition.Top}
                            >
                                <FontAwesomeIcon
                                    icon={faClock as IconProp}
                                    className="c-PaymentForm-PaymentPlans-payTotalAmount-onhold-icon"
                                />
                            </Tooltip>
                        ) : undefined}
                        {fm(
                            isPayNow
                                ? "paymentPlans.payTotalAmount"
                                : "paymentPlans.payTotalAmountOnDeadline"
                        )}
                        <span className="c-PaymentForm-PaymentPlans-plans-payTotalAmount-dueDate">
                            {fm({
                                id: isPayNow
                                    ? "paymentPlans.payTotalAmountDueDate"
                                    : "paymentPlans.payTotalAmountOnDueDate",
                                values: {
                                    dueDate: (
                                        <FormattedDate
                                            value={
                                                new Date(
                                                    singleInstallmentPaymentPlan.installments[0].dueDate
                                                )
                                            }
                                        />
                                    ),
                                },
                            })}
                        </span>
                    </>
                ),
                value: singleInstallmentPaymentPlan.id,
                disabled:
                    !isEmpty(paymentRequest.payments) &&
                    some(
                        paymentRequest.payments,
                        (payment) =>
                            payment.paymentPlanId !==
                                singleInstallmentPaymentPlan.id &&
                            (payment.status === PaymentStatus.Paid ||
                                payment.status === PaymentStatus.Scheduled ||
                                payment.status === PaymentStatus.OnHold)
                    ),
            });
        }

        if (!isNil(multipleInstallmentPaymentPlan)) {
            options.push({
                label: (
                    <>
                        {fm(
                            isPayNow
                                ? "paymentPlans.payInInstallments"
                                : "paymentPlans.payInInstallmentsOnDeadline"
                        )}
                        <span className="c-PaymentForm-PaymentPlans-plans-payInInstallments-info">
                            {fm(
                                isPayNow
                                    ? "paymentPlans.payInInstallmentsInfo"
                                    : "paymentPlans.payInInstallmentsonDeadlineInfo"
                            )}
                        </span>
                    </>
                ),
                value: multipleInstallmentPaymentPlan.id,
                disabled:
                    !isEmpty(paymentRequest.payments) &&
                    some(
                        paymentRequest.payments,
                        (payment) =>
                            payment.paymentPlanId !==
                                multipleInstallmentPaymentPlan.id &&
                            (payment.status === PaymentStatus.Paid ||
                                payment.status === PaymentStatus.Scheduled ||
                                payment.status === PaymentStatus.OnHold)
                    ),
            });
        }

        return options;
    }

    function getInstallmentsOptions() {
        const multipleInstallmentPaymentPlanInstallments =
            getInstallmentsSortedByDueDate(
                multipleInstallmentPaymentPlan?.installments
            );

        return multipleInstallmentPaymentPlanInstallments.map((installment) => {
            const isAlreadyPaidOrScheduledOrHold = some(
                paymentRequest.payments,
                (payment) =>
                    (payment.status === PaymentStatus.Paid ||
                        payment.status === PaymentStatus.Scheduled ||
                        payment.status === PaymentStatus.OnHold) &&
                    includes(payment.installmentIds, installment.id)
            );

            const isExpired =
                compareAsc(
                    new Date(),
                    endOfDay(new Date(installment.dueDate))
                ) === 1;

            const isOnHold = some(
                paymentRequest.payments,
                (payment) =>
                    payment.status === PaymentStatus.OnHold &&
                    includes(payment.installmentIds, installment.id)
            );

            return (
                <Checkbox
                    key={installment.id}
                    className={classNames(
                        "c-PaymentForm-PaymentPlans-installments-checkbox",
                        isOnHold ? "onHold" : undefined
                    )}
                    label={
                        <div
                            className={classNames(
                                "c-PaymentForm-PaymentPlans-installments-checkbox-label",
                                !isPayNow &&
                                    !isAlreadyPaidOrScheduledOrHold &&
                                    selectedPaymentPlanId ===
                                        multipleInstallmentPaymentPlan!.id
                                    ? "c-PaymentForm-PaymentPlans-installments-checkbox-label-pay-on-deadline-still-active"
                                    : undefined,
                                !isAlreadyPaidOrScheduledOrHold && isExpired
                                    ? "c-PaymentForm-PaymentPlans-installments-checkbox-label-expired"
                                    : undefined
                            )}
                        >
                            {isOnHold ? (
                                <Tooltip
                                    text={fm("onHoldTooltip")}
                                    position={TooltipTextPosition.Top}
                                >
                                    <FontAwesomeIcon
                                        icon={faClock as IconProp}
                                        className="c-PaymentForm-PaymentPlans-installments-checkbox-label-onhold-icon"
                                    />
                                </Tooltip>
                            ) : undefined}
                            {fm({
                                id: `installments.installment`,
                                values: {
                                    dueDate: (
                                        <FormattedDate
                                            value={
                                                new Date(installment.dueDate)
                                            }
                                        />
                                    ),
                                    amount: (
                                        <FormattedNumber
                                            style="currency"
                                            currency={
                                                installment.amount.currency
                                            }
                                            value={numbro(
                                                installment.amount.amount
                                            ).value()}
                                        />
                                    ),
                                },
                            })}
                        </div>
                    }
                    checked={includes(selectedInstallmentIds, installment.id)}
                    onChange={() => addOrRemoveInstallmentId(installment.id)}
                    disabled={
                        selectedPaymentPlanId !==
                            multipleInstallmentPaymentPlan!.id ||
                        isAlreadyPaidOrScheduledOrHold ||
                        !isPayNow
                    }
                />
            );
        });
    }

    return (
        <>
            <div className="c-PaymentForm-form-title">
                {fm("title")} <PrivacyCookiePolicyMenu />
            </div>
            <div className="c-PaymentForm-PaymentPlans-payNowToggle-container">
                {width > 1000 ? (
                    <>
                        <Toggle
                            className="c-PaymentForm-PaymentPlans-payNowToggle-toggle"
                            checked={isPayNow}
                            onChange={changePayNow}
                            disabled={
                                paymentRequest.status ===
                                PaymentRequestStatus.Scheduled
                            }
                        />
                        <span className="c-PaymentForm-PaymentPlans-payNowToggle-toggle-label">
                            {fm("payNow")}
                        </span>
                        <div className="c-PaymentForm-PaymentPlans-payNowToggle-toggle-infoIcon-container">
                            <Tooltip
                                text={fm("payNowInfoTooltip")}
                                position={TooltipTextPosition.Top}
                            >
                                <FontAwesomeIcon
                                    className="c-PaymentForm-PaymentPlans-payNowToggle-toggle-infoIcon"
                                    icon={faInfoCircle as IconProp}
                                />
                            </Tooltip>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="c-PaymentForm-PaymentPlans-payNowToggle-divider" />
                        <div className="c-PaymentForm-PaymentPlans-payNowToggle-content">
                            <div className="c-PaymentForm-PaymentPlans-payNowToggle-toggle-infoIcon-container">
                                <Tooltip
                                    text={fm("payNowInfoTooltip")}
                                    position={TooltipTextPosition.Top}
                                >
                                    <FontAwesomeIcon
                                        className="c-PaymentForm-PaymentPlans-payNowToggle-toggle-infoIcon"
                                        icon={faInfoCircle as IconProp}
                                    />
                                </Tooltip>
                            </div>
                            <span className="c-PaymentForm-PaymentPlans-payNowToggle-toggle-label">
                                {fm("payNow")}
                            </span>
                            <Toggle
                                className="c-PaymentForm-PaymentPlans-payNowToggle-toggle"
                                checked={isPayNow}
                                onChange={changePayNow}
                                disabled={
                                    paymentRequest.status ===
                                    PaymentRequestStatus.Scheduled
                                }
                            />
                        </div>
                    </>
                )}
            </div>
            <div className="c-PaymentForm-PaymentPlans-plans">
                <RadioGroup
                    type="vertical"
                    className={classNames(
                        "c-PaymentForm-PaymentPlans-plans-radioGroup",
                        some(getPaymentPlansOptions(), { onHold: true })
                            ? "c-PaymentForm-PaymentPlans-plans-radioGroup-paytotal-onHold"
                            : undefined
                    )}
                    value={selectedPaymentPlanId}
                    onChange={changePaymentPlanId}
                    options={getPaymentPlansOptions()}
                />
            </div>
            {!isNil(multipleInstallmentPaymentPlan) &&
            (width > 1000 ||
                selectedPaymentPlanId === multipleInstallmentPaymentPlan.id) ? (
                <div className="c-PaymentForm-PaymentPlans-installments">
                    {getInstallmentsOptions()}
                </div>
            ) : undefined}
        </>
    );
};
